import { computed, DestroyRef, inject, Service, signal } from '@angular/core';

import { ActionState } from '@shared/state/async-action';
import { PlatformService } from '@core/platform/platform.service';
import { OrganizationsRepository } from '../domain/interfaces/organizations.interface';
import {
  EMPTY_COUNSELING_OPTIONS,
  ORG_CANCEL_REASON_MAX,
  OrgCounselingOptions,
  OrgCounselingSession,
  OrgCounselingSlot,
  hasBookingShape,
  isOpenSession,
  isProxyStage,
} from '../domain/models/org-counseling.model';
import { OrgError, isRetryableWithAnotherSlot } from '../domain/org.errors';
import { OrgErrorMapper } from './org-error.mapper';
import { destroyNotifier, firstValueFromScoped, runOrgAction } from './org-action';

/**
 * OrgCounselingFacade — «الرعاية والإرشاد» (§7).
 *
 * Schools product only. The provider is the school's own employee, the time is a
 * school period, and a child's booking may wait on a guardian.
 *
 * Three things here are not ordinary CRUD:
 *
 *  · Trap 12 — `GET /counseling/options` has TWO SHAPES. The proxy-stage payload
 *    (kindergarten: the child has no account to book from) carries no `slots`,
 *    `guardian`, `cost` or `checklist` at all. {@link canBook} and
 *    {@link isProxy} branch on that before any field is read.
 *  · Trap 13 — `slot_full` is a NORMAL OUTCOME, not an error. Thirty students
 *    share one counselor and one break, so {@link shouldPickAnotherSlot} turns it
 *    into "that one just went — pick another", with the slots re-read so the
 *    taken one is gone.
 *  · §7.2 — the success `data.message` is a ready-to-render sentence that differs
 *    by whether consent was needed. It is shown VERBATIM, never re-derived.
 */
@Service({ autoProvided: false })
export class OrgCounselingFacade {
  private readonly repo = inject(OrganizationsRepository);
  private readonly errors = inject(OrgErrorMapper);
  private readonly platform = inject(PlatformService);
  private readonly destroyed = destroyNotifier(inject(DestroyRef));

  private readonly _options = signal<OrgCounselingOptions>(EMPTY_COUNSELING_OPTIONS);
  private readonly _sessions = signal<readonly OrgCounselingSession[]>([]);
  private readonly _optionsError = signal<OrgError | null>(null);
  private readonly _sessionsError = signal<OrgError | null>(null);
  private readonly _bookError = signal<OrgError | null>(null);
  private readonly _cancelError = signal<OrgError | null>(null);
  private readonly _optionsLoadedAt = signal(0);
  private readonly _sessionsLoadedAt = signal(0);
  /** The slot the member picked — the booking CTA acts on this. */
  private readonly _selectedSlotId = signal<number | null>(null);
  /** The session a cancel is running for, so only that row shows busy. */
  private readonly _cancellingId = signal<number | null>(null);

  readonly optionsLoad = new ActionState('reference_portal_org_counseling_options_failed', 'GET /counseling/options');
  readonly sessionsLoad = new ActionState('reference_portal_org_counseling_sessions_failed', 'GET /counseling/bookings');
  readonly book = new ActionState('reference_portal_org_counseling_book_failed', 'POST /counseling/bookings');
  readonly cancel = new ActionState('reference_portal_org_counseling_cancel_failed', 'POST /counseling/bookings/{id}/cancel');

  readonly options = this._options.asReadonly();
  readonly sessions = this._sessions.asReadonly();
  readonly optionsError = this._optionsError.asReadonly();
  readonly sessionsError = this._sessionsError.asReadonly();
  readonly bookError = this._bookError.asReadonly();
  readonly cancelError = this._cancelError.asReadonly();
  readonly selectedSlotId = this._selectedSlotId.asReadonly();
  readonly cancellingId = this._cancellingId.asReadonly();

  readonly isFirstOptionsLoad = computed(() => this._optionsLoadedAt() === 0 && !this.optionsLoad.failed());
  readonly isFirstSessionsLoad = computed(() => this._sessionsLoadedAt() === 0 && !this.sessionsLoad.failed());

  /** Kindergarten: the meeting is arranged through the guardian and the teacher. */
  readonly isProxy = computed(() => isProxyStage(this._options()));
  /** True when the payload carried the booking furniture — false for the proxy shape. */
  readonly hasBookingFurniture = computed(() => hasBookingShape(this._options()));
  /** Slots exist AND the server says booking is on. */
  readonly canBook = computed(() => this._options().available && this._options().slots.length > 0);
  readonly slots = computed(() => this._options().slots);
  readonly counselors = computed(() => this._options().counselors);
  /**
   * Shown BEFORE the student picks a time (§7.1): consent is required for the
   * `full`/`summary` tiers and not for `minimal` (high school).
   */
  readonly guardianPolicy = computed(() => this._options().guardian);
  readonly cost = computed(() => this._options().cost);
  readonly checklist = computed(() => this._options().checklist);
  /** Ready-to-render explanation when booking is off — `proxy_stage` or `no_slots`. */
  readonly unavailableMessage = computed(() => this._options().message);

  readonly selectedSlot = computed<OrgCounselingSlot | null>(
    () => this.slots().find((s) => s.slotId === this._selectedSlotId()) ?? null,
  );
  readonly openSessions = computed(() => this._sessions().filter(isOpenSession));
  /** §7.2 `already_booked`: one open session at a time — the UI says so up front. */
  readonly hasOpenSession = computed(() => this.openSessions().length > 0);

  /** Trap 13 — a lost race, not a failure. The view says "pick another", not "error". */
  readonly shouldPickAnotherSlot = computed(() => isRetryableWithAnotherSlot(this._bookError()?.reason ?? null));

  readonly cancelReasonMaxLength = ORG_CANCEL_REASON_MAX;

  // ── reads ───────────────────────────────────────────────────────────────
  async ensureLoaded(): Promise<void> {
    if (!this.platform.isBrowser) return;
    await Promise.all([this.loadOptions(), this.loadSessions()]);
  }

  async loadOptions(): Promise<void> {
    if (this.optionsLoad.loading()) return;
    const options = await runOrgAction(this.optionsLoad, this._optionsError, this.errors, () =>
      firstValueFromScoped(this.repo.getCounselingOptions(), this.destroyed),
    );
    if (options) {
      this._options.set(options);
      this._optionsLoadedAt.set(Date.now());
      // A slot that vanished between reads must not stay selected — the CTA would
      // then post a `slot_id` the server has already retired.
      if (!options.slots.some((s) => s.slotId === this._selectedSlotId())) this._selectedSlotId.set(null);
    }
  }

  async loadSessions(): Promise<void> {
    if (this.sessionsLoad.loading()) return;
    const sessions = await runOrgAction(this.sessionsLoad, this._sessionsError, this.errors, () =>
      firstValueFromScoped(this.repo.getCounselingSessions(), this.destroyed),
    );
    if (sessions) {
      this._sessions.set(sessions);
      this._sessionsLoadedAt.set(Date.now());
    }
  }

  // ── writes ──────────────────────────────────────────────────────────────
  selectSlot(slotId: number): void {
    this._selectedSlotId.set(slotId);
    this._bookError.set(null);
    this.book.reset();
  }

  /**
   * Book the selected slot. Both `slot_id` and `date` are required and come from
   * the SAME slot row, so the (slot × date) pair is always one the server offered.
   *
   * On `slot_full` the options are re-read before returning, so the taken slot is
   * gone from the list the member is looking at when they pick again.
   */
  async bookSelected(): Promise<boolean> {
    const slot = this.selectedSlot();
    if (!slot) return false;

    const result = await runOrgAction(this.book, this._bookError, this.errors, () =>
      firstValueFromScoped(this.repo.bookCounseling(slot.slotId, slot.date), this.destroyed),
    );

    if (!result) {
      // Trap 13: the world moved rather than the request being wrong. Refresh the
      // offer so the retry is against reality.
      if (this.shouldPickAnotherSlot()) {
        this._selectedSlotId.set(null);
        await this.loadOptions();
      }
      return false;
    }

    this._sessions.set(result.sessions);
    this._sessionsLoadedAt.set(Date.now());
    this._selectedSlotId.set(null);
    // Ready to render, and it differs by whether consent was needed — shown
    // verbatim through the shared toast (§7.2).
    if (result.message) this.errors.success(result.message);
    // The booked slot is now one seat fuller, and may be full.
    await this.loadOptions();
    return true;
  }

  /**
   * Cancel an open session. Only pending/confirmed can be cancelled; anything
   * else is a 404 carrying `data.reason`, which the mapper turns into copy.
   */
  async cancelSession(sessionId: number, reason?: string | null): Promise<boolean> {
    this._cancellingId.set(sessionId);
    try {
      const sessions = await runOrgAction(this.cancel, this._cancelError, this.errors, () =>
        firstValueFromScoped(this.repo.cancelCounseling(sessionId, reason), this.destroyed),
      );
      if (!sessions) return false;
      this._sessions.set(sessions);
      // A freed seat changes what is on offer.
      await this.loadOptions();
      return true;
    } finally {
      this._cancellingId.set(null);
    }
  }

  clearBookError(): void {
    this._bookError.set(null);
    this.book.reset();
  }

  clearCancelError(): void {
    this._cancelError.set(null);
    this.cancel.reset();
  }
}
